/** Pure Sponsored campaign list/inspect display mapping — no I/O. D4E. */

import { CTA_TYPE_LABELS, isEligibleForPublicApi, type CtaType } from "@vital/shared";
import type { StatusTone } from "./overview-ops";
import {
  CONTENT_STATE_LABELS,
  FILTER_ALL,
  FILTER_PAUSED,
  FILTER_QUEUE,
} from "./content-state";

export type SponsoredCampaignRow = {
  id: string;
  advertiserId: string;
  advertiserName: string;
  advertiserStatus: string;
  name: string;
  headline: string;
  description: string;
  offerText: string | null;
  sponsorshipLabel: string;
  state: string;
  pausedFromState: string | null;
  priority: number;
  scope: string;
  siteCode: string | null;
  siteName: string | null;
  groupId: string | null;
  groupName: string | null;
  startsAt: string;
  endsAt: string;
  rejectionReason: string | null;
  submittedAt: string | null;
  createdAt?: string;
  updatedAt?: string;
  imagePath: string;
  imageAlt: string;
  ctaType: CtaType | string | null;
  ctaLabel: string;
  ctaHref: string;
};

export type SponsoredSortKey = "updated" | "title" | "priority" | "advertiser";

export type EligibilityDisplay = {
  label: string;
  tone: StatusTone;
  detail?: string;
};

export type AssetStateDisplay = {
  label: string;
  tone: StatusTone;
};

export function sponsoredStateLabelFa(state: string): string {
  return CONTENT_STATE_LABELS[state] ?? state;
}

export function sponsoredCtaLabelFa(
  ctaType: string | null | undefined,
  ctaLabel?: string | null,
): string {
  if (!ctaType) return "بدون CTA";
  const typeLabel = (CTA_TYPE_LABELS as Record<string, string>)[ctaType] ?? ctaType;
  const label = ctaLabel?.trim();
  return label ? `${typeLabel} — ${label}` : typeLabel;
}

export function sponsoredCtaTone(ctaType: string | null | undefined): StatusTone {
  return ctaType ? "info" : "neutral";
}

export function sponsoredCreativeState(imagePath: string | null | undefined): AssetStateDisplay {
  if (imagePath?.trim()) return { label: "خلاقه دارد", tone: "success" };
  return { label: "بدون خلاقه", tone: "danger" };
}

export function advertiserStatusLabelFa(status: string): string {
  if (status === "active") return "فعال";
  if (status === "archived") return "بایگانی";
  return status;
}

export function isSponsoredWindowExpired(endsAt: string, now: Date = new Date()): boolean {
  const end = Date.parse(endsAt);
  return Number.isFinite(end) && end <= now.getTime();
}

/**
 * Honest eligibility from lifecycle + schedule + existing entitlement/advertiser flags.
 * Does not invent impression or resolver output.
 */
export function sponsoredEligibility(
  row: Pick<
    SponsoredCampaignRow,
    "state" | "startsAt" | "endsAt" | "advertiserStatus"
  >,
  opts: { sponsoredAllowed: boolean } = { sponsoredAllowed: true },
  now: Date = new Date(),
): EligibilityDisplay {
  if (!opts.sponsoredAllowed) {
    return {
      label: "طرح سازمان — بدون تبلیغات ویژه",
      tone: "warning",
      detail: "دسترسی پلان آگهی پولی را مجاز نمی‌کند",
    };
  }
  if (row.advertiserStatus === "archived") {
    return { label: "تبلیغ‌کننده بایگانی", tone: "warning" };
  }
  if (row.state === "draft" || row.state === "pending_approval") {
    return { label: "منتشر نشده", tone: "neutral", detail: sponsoredStateLabelFa(row.state) };
  }
  if (row.state === "paused") {
    return { label: "متوقف — بدون نمایش عمومی", tone: "warning" };
  }
  if (row.state === "archived") {
    return { label: "بایگانی — بدون نمایش عمومی", tone: "neutral" };
  }
  if (row.state === "expired" || isSponsoredWindowExpired(row.endsAt, now)) {
    return { label: "منقضی — بدون نمایش عمومی", tone: "neutral" };
  }
  if (row.state === "published" || row.state === "scheduled") {
    const starts = row.startsAt ? new Date(row.startsAt) : null;
    const ends = row.endsAt ? new Date(row.endsAt) : null;
    const ok = isEligibleForPublicApi(starts, ends, now);
    if (ok) {
      return {
        label: "واجد شرایط نمایش عمومی",
        tone: "success",
        detail: row.state === "scheduled" ? "زمان‌بندی‌شده و داخل بازه" : "منتشرشده و داخل بازه",
      };
    }
    return {
      label: "خارج از پنجرهٔ زمانی",
      tone: "warning",
      detail: sponsoredStateLabelFa(row.state),
    };
  }
  return { label: "نامشخص", tone: "neutral", detail: row.state };
}

export function matchesSponsoredQuery(row: SponsoredCampaignRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    row.name,
    row.headline,
    row.description,
    row.advertiserName,
    row.state,
    sponsoredStateLabelFa(row.state),
    row.ctaType ?? "",
    sponsoredCtaLabelFa(row.ctaType, row.ctaLabel),
    row.ctaHref ?? "",
    row.siteCode ?? "",
    row.siteName ?? "",
    row.groupName ?? "",
    row.scope,
    row.offerText ?? "",
    String(row.priority),
  ]
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

export function matchesSponsoredListFilter(
  row: SponsoredCampaignRow,
  listFilter: string,
): boolean {
  if (listFilter === FILTER_ALL) return true;
  return row.state === listFilter;
}

export function filterAndSortSponsoredCampaigns(
  rows: SponsoredCampaignRow[],
  opts: { query: string; listFilter: string; sort: SponsoredSortKey },
): SponsoredCampaignRow[] {
  const filtered = rows.filter(
    (row) =>
      matchesSponsoredListFilter(row, opts.listFilter) &&
      matchesSponsoredQuery(row, opts.query),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "title") {
      return a.name.localeCompare(b.name, "fa", { sensitivity: "base" });
    }
    if (opts.sort === "advertiser") {
      return a.advertiserName.localeCompare(b.advertiserName, "fa", { sensitivity: "base" });
    }
    if (opts.sort === "priority") {
      return b.priority - a.priority || a.name.localeCompare(b.name, "fa");
    }
    const au = a.updatedAt ?? a.submittedAt ?? a.createdAt ?? "";
    const bu = b.updatedAt ?? b.submittedAt ?? b.createdAt ?? "";
    return bu.localeCompare(au);
  });
  return sorted;
}

export type RelatedSponsoredLink = { id: string; label: string; to: string };

export function relatedSponsoredLinks(): RelatedSponsoredLink[] {
  return [
    { id: "sites-preview", label: "پیش‌نمایش مؤثر (سایت‌ها)", to: "/sites#effective-preview" },
    { id: "announcements", label: "اطلاعیه‌ها", to: "/announcements" },
    { id: "promotions", label: "پیشنهادها", to: "/promotions" },
    { id: "entitlements", label: "دسترسی پلان", to: "/entitlements" },
    { id: "settings-cta", label: "فهرست CTA", to: "/settings" },
    { id: "audit", label: "سابقه تغییرات", to: "/audit" },
  ];
}

export { FILTER_ALL, FILTER_QUEUE, FILTER_PAUSED };

/** Preset sponsorship disclosure labels (plain text, required on portal). */
export const SPONSORED_SPONSORSHIP_LABEL_PRESETS = [
  "آگهی",
  "حمایت‌شده",
  "تبلیغ",
  "آگهی حمایت‌شده",
  "اسپانسر",
] as const;

export type SponsoredSponsorshipLabelPreset = (typeof SPONSORED_SPONSORSHIP_LABEL_PRESETS)[number];

export function sponsoredSponsorshipLabelOptions(current?: string | null): string[] {
  const trimmed = current?.trim() ?? "";
  if (!trimmed) return [...SPONSORED_SPONSORSHIP_LABEL_PRESETS];
  if ((SPONSORED_SPONSORSHIP_LABEL_PRESETS as readonly string[]).includes(trimmed)) {
    return [...SPONSORED_SPONSORSHIP_LABEL_PRESETS];
  }
  return [trimmed, ...SPONSORED_SPONSORSHIP_LABEL_PRESETS];
}

export function isSponsoredSponsorshipLabelPreset(
  value: string,
): value is SponsoredSponsorshipLabelPreset {
  return (SPONSORED_SPONSORSHIP_LABEL_PRESETS as readonly string[]).includes(value);
}

/** Muted inventory line: headline · advertiser · CTA */
export function sponsoredCampaignTitleMeta(row: SponsoredCampaignRow): string {
  const parts = [row.headline.trim(), row.advertiserName.trim(), sponsoredCtaLabelFa(row.ctaType, row.ctaLabel)];
  return parts.filter(Boolean).join(" · ");
}

export function sponsoredCreativeFilename(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return trimmed;
  const parts = trimmed.split("/");
  return parts[parts.length - 1] ?? trimmed;
}
